<script lang="ts">
	import type { Mediator } from 'types/mediator'
	import type { SvelteAsr } from 'types/common'
	import type { i18n } from 'types/common'

	import { getContext, onDestroy, type ComponentProps } from 'svelte'
	import Setting from '@isoftdata/svelte-setting'
	import SaveResetButton from '@isoftdata/svelte-save-reset-button'
	import { booleanToString } from '@isoftdata/utility-boolean'
	import { stringToBoolean } from '@isoftdata/utility-string'
	import { graphql, type SettingsDefaultValueUpdate, type SettingsValueUpdate } from '$houdini'

	interface Props {
		asr: SvelteAsr
		selectedCategory: string
		settings: ComponentProps<typeof Setting>['settings']
		settingTypeFilters: Array<{
			name: string
			value: boolean
			realSettingName: string
		}>
	}

	let {
		//
		asr,
		selectedCategory = $bindable(),
		settings = $bindable([]),
		settingTypeFilters = $bindable([]),
	}: Props = $props()

	let settingsComponent: Setting | undefined = $state()

	const { t: translate } = getContext<i18n>('i18next')
	const mediator = getContext<Mediator>('mediator')

	const updateSettingValuesMutation = graphql(`
		mutation UpdateSettingValue($input: SettingsValueUpdate!) {
			updateSettingValue(input: $input)
		}
	`)

	const updateDefaultSettingValuesMutation = graphql(`
		mutation UpdateSettingDefaultValue($input: SettingsDefaultValueUpdate!) {
			updateSettingDefaultValue(input: $input)
		}
	`)

	function formatSettingValue(value: string | number | boolean | null): string {
		if (typeof value === 'boolean') {
			return booleanToString(value)
		}

		// for somes reason the component gives us these as a string sometimes
		if (typeof value === 'string' && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
			return booleanToString(stringToBoolean(value))
		}

		return value?.toString() ?? ''
	}

	function formatSettingForSave([settingId, value]: [
		number,
		string | number | boolean | null,
	]): SettingsValueUpdate['settings'][number] {
		return {
			settingId,
			value: formatSettingValue(value),
		}
	}

	function formatDefaultSettingForSave([settingId, defaultValue]: [
		number,
		string | number | boolean | null,
	]): SettingsDefaultValueUpdate['settings'][number] {
		return {
			settingId,
			defaultValue: formatSettingValue(defaultValue),
		}
	}

	async function saveSettings() {
		if (!updatedDefaultValues || !updatedValues) {
			return
		}

		try {
			await Promise.all([
				updatedDefaultValues.size
					? updateDefaultSettingValuesMutation.mutate({
							input: {
								settings: Array.from(updatedDefaultValues.entries()).map(formatDefaultSettingForSave),
							},
						})
					: null,
				updatedValues.size
					? updateSettingValuesMutation.mutate({
							input: {
								settings: Array.from(updatedValues.entries()).map(formatSettingForSave),
							},
						})
					: null,
			])

			mediator.publish('showMessage', {
				heading: translate('common:savedHeading', 'Saved!'),
				message: translate('configuration.settings.successfullySavedSettings', 'Successfully saved settings'),
				color: 'success',
				time: 10,
			})

			settingsChanged = false
			asr.go(null, { lastSavedTime: Date.now(), selectedCategory })
		} catch (err: any) {
			mediator.publish('showMessage', {
				heading: translate('configuration.settings.errorSavingSettings', 'Error Saving Settings'),
				message: err.message,
				color: 'danger',
				time: false,
			})
		}
	}

	async function saveInterfaceHistory(): Promise<void> {
		// Find the settings that control the interface history
		const settingsToFindNames = settingTypeFilters.map(choice => choice.realSettingName)
		const mappedSettings = settingsToFindNames
			.map(setting => {
				const originalSetting = settings.find(s => s.name === setting)
				if (!originalSetting) {
					return null
				}
				if (originalSetting.value !== null) {
					// These settings are all booleans anyways
					const originalValue = stringToBoolean(originalSetting.value.toString())
					const newValue = settingTypeFilters.find(choice => choice.realSettingName === originalSetting.name)?.value
					if (originalValue !== newValue) {
						originalSetting.value = booleanToString(newValue ?? true)
					}
				}
				return formatSettingForSave([originalSetting.id, originalSetting.value])
			})
			.filter(s => !!s)
		await updateSettingValuesMutation.mutate({
			input: {
				settings: mappedSettings,
			},
		})
	}

	export function canLeaveState() {
		return (
			!settingsChanged ||
			confirm(
				translate(
					'common:youHaveUnsavedChangesAreYouSureYouWantToLeave',
					'You have unsaved changes. Are you sure you want to leave?',
				),
			)
		)
	}

	const updatedDefaultValues = $derived(settingsComponent?.updatedDefaultValues ?? null)
	const updatedValues = $derived(settingsComponent?.updatedValues ?? null)
	let settingsChanged: boolean = $derived(!!updatedDefaultValues?.size || !!updatedValues?.size)

	$effect(() => {
		asr.go(null, { selectedCategory }, { inherit: true })
	})

	onDestroy(() => {
		saveInterfaceHistory()
	})
</script>

<div class="card">
	<div class="card-header">
		<div class="row">
			<div class="col">
				<h4 class="card-title">
					{translate('configuration.settings.settingsConfiguration', 'Settings Configuration')}
				</h4>
			</div>
			<div class="col text-end">
				<SaveResetButton
					disabled={!settingsChanged}
					resetHref={asr.makePath(null, { lastResetTime: Date.now(), selectedCategory })}
					save={() => saveSettings()}
				/>
			</div>
		</div>
	</div>
	<div class="p-2">
		<Setting
			hideDescription
			defaultValueEditable
			showSettingScope
			{settingTypeFilters}
			siteAlias={translate('common:store', 'Store')}
			bind:settings
			bind:selectedCategory
			bind:this={settingsComponent}
		/>
	</div>
</div>
